import { Command } from "commander";
import { discoverFlows } from "../flow-discovery.ts";
import { logErr, logInfo, S } from "../helpers.ts";

/**
 * Create the `skaile flow` command group.
 *
 * Browses flows discovered across every content root via {@link discoverFlows}
 * (bundled factory-assets when installed, the monorepo tree in dev, plus user
 * libraries). Subcommands: `list` (print all available flows with
 * their IDs, names, and descriptions) and `show <id>` (print the flow's
 * metadata and its skill nodes in execution order, marking optional nodes).
 *
 * @returns Configured {@link Command} ready for `program.addCommand()`.
 * @docLink cli/commands/flow#make-flow-command
 */
export function makeFlowCommand(): Command {
  const cmd = new Command("flow").description("Manage flows");

  cmd
    .command("list")
    .description("List available flows")
    .action(async () => {
      const flows = discoverFlows();
      if (flows.length === 0) {
        logInfo("No flows found.");
        return;
      }

      console.log();
      console.log(S.heading("  Available Flows"));
      console.log(`  ${S.rule(50)}`);
      for (const f of flows) {
        console.log(`  ${S.cmd(f.id.padEnd(24))} ${f.name}`);
        if (f.description) console.log(`  ${"".padEnd(24)} ${S.dim(f.description)}`);
      }
      console.log();
    });

  cmd
    .command("show <id>")
    .description("Show flow details and node graph")
    .action(async (id: string) => {
      const flows = discoverFlows();
      const flow = flows.find((f) => f.id === id);
      if (!flow) {
        logErr(`Flow not found: ${id}`);
        process.exit(1);
      }

      console.log();
      console.log(`  ${S.heading(flow.name)} ${S.dim(`(${flow.id} v${flow.version})`)}`);
      if (flow.description) console.log(`  ${S.dim(flow.description)}`);
      const skillNodes = flow.nodes.filter((n) => n.type === "skill");
      console.log(`\n  ${S.heading("Nodes")} ${S.dim(`(${skillNodes.length} skills)`)}`);
      console.log(`  ${S.rule(50)}`);
      for (const node of skillNodes) {
        const opt = node.data?.optional ? S.dim(" [optional]") : "";
        const label = (node.data?.label ?? node.id).padEnd(24);
        console.log(`  ${label} ${S.label("skill:")} ${S.cmd(node.data?.skill ?? node.id)}${opt}`);
      }
      console.log();
    });

  cmd.action(() => cmd.help());

  return cmd;
}
